import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

const statusColor = (s) => {
  if (s === 'completed') return '#059669';
  if (s === 'failed' || s === 'permanently_failed' || s === 'completed_with_errors') return '#dc2626';
  if (s === 'processing' || s === 'pending') return '#d97706';
  return '#6b7280';
};
const fmt = (s) => (s ? new Date(s).toLocaleString() : '—');

export default function AdminFileUpload() {
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState('');   // dealer_code
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const fileInput = useRef(null);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);
  const flash = (m) => { setMessage(m); setTimeout(() => setMessage(''), 3000); };

  useEffect(() => {
    const h = authHeader();
    if (!h) return;
    fetch(`${API_BASE}/admin/dealers?sftp_only=true&limit=500`, { headers: h })
      .then(adminJson)
      .then(d => setDealers((Array.isArray(d) ? d : (d.dealers || d.dealerships || []))))
      .catch(() => setError('Failed to load dealers'));
  }, [authHeader]);

  const loadImports = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    setLoading(true);
    const qs = new URLSearchParams({ limit: '50' });
    if (dealerId) qs.append('dealer_id', dealerId);
    fetch(`${API_BASE}/admin/imports?${qs}`, { headers: h })
      .then(adminJson)
      .then(d => setImports(d.imports || []))
      .catch(() => setError('Failed to load imports'))
      .finally(() => setLoading(false));
  }, [authHeader, dealerId]);

  useEffect(() => { loadImports(); }, [loadImports]);

  const onPick = () => {
    if (!dealerId) { setError('Select a dealership first'); return; }
    fileInput.current?.click();
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const h = authHeader();
    if (!h) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('dealership_id', dealerId);
      const res = await fetch(`${API_BASE}/admin/imports`, { method: 'POST', headers: h, body: fd });
      if (!res.ok) { const e2 = await res.json().catch(() => ({})); setError(e2.detail || 'Upload failed'); return; }
      flash(`Uploaded ${f.name} — processing will pick it up shortly`);
      loadImports();
    } catch (e2) { setError('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ padding: 20, maxWidth: 1150, margin: '0 auto' }}>
      <input ref={fileInput} type="file" onChange={onFile} style={{ display: 'none' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>DMS File Upload</h2>
      </div>
      <p style={{ color: '#6b7280', marginTop: 0 }}>Upload a DMS inventory file for an SFTP-enabled dealer. The scheduler auto-detects the format and imports it within ~60s.</p>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error} <button onClick={() => setError('')} style={linkBtn}>dismiss</button></div>}
      {message && <div style={{ color: '#059669', marginBottom: 8 }}>{message}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
        <select value={dealerId} onChange={e => setDealerId(e.target.value)} style={ctrl}>
          <option value="">Select dealership (SFTP-enabled)…</option>
          {dealers.map(d => <option key={d.id} value={d.dealer_code || d.dynamodb_dealership_id}>{d.dealership_name || d.name}</option>)}
        </select>
        <button onClick={onPick} disabled={uploading || !dealerId} style={primaryBtn}>{uploading ? 'Uploading…' : '⬆ Upload DMS file'}</button>
        <div style={{ flex: 1 }} />
        <button onClick={loadImports} style={btn}>Refresh history</button>
      </div>

      <h3 style={{ marginBottom: 6 }}>Import history{dealerId ? ' (this dealer)' : ''}</h3>
      {loading ? <div>Loading…</div> : (
        <table style={table}>
          <thead><tr>{['File', 'Dealer', 'Format', 'Status', 'Found', 'Loaded', 'Updated', 'Skipped', 'Errors', 'When'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {imports.map(i => (
              <tr key={i.id} style={{ borderBottom: '1px solid #eef2f7' }}>
                <td style={td}>{i.filename}</td>
                <td style={td}>{i.dealership_name}</td>
                <td style={td}>{i.detected_format || '—'}</td>
                <td style={{ ...td, color: statusColor(i.import_status), fontWeight: 600 }}>{i.import_status}</td>
                <td style={td}>{i.records_found ?? '—'}</td>
                <td style={td}>{i.records_imported ?? '—'}</td>
                <td style={td}>{i.records_updated ?? '—'}</td>
                <td style={td}>{i.records_skipped ?? '—'}</td>
                <td style={{ ...td, color: i.error_count ? '#dc2626' : '#111827' }}>{i.error_count ?? 0}</td>
                <td style={td}>{fmt(i.created_at)}</td>
              </tr>
            ))}
            {!imports.length && <tr><td colSpan={10} style={{ ...td, color: '#6b7280' }}>No imports.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

const ctrl = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6, minWidth: 280 };
const btn = { padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const primaryBtn = { ...btn, background: '#0078D4', color: '#fff', border: 'none' };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { textAlign: 'left', padding: '7px 8px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '7px 8px', verticalAlign: 'top' };
